import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react";
import { useLang, type Language } from "@/i18n";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: t.nav.home, href: "#hero" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.kols, href: "#kols" },
    { label: t.nav.howWeWork, href: "#how-we-work" },
    { label: t.nav.contact, href: "#contact" },
  ];

  const langOptions: { code: Language; label: string }[] = [
    { code: "en", label: "EN" },
    { code: "zh", label: "中文" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        height: 80,
        transition: "background 0.5s ease, border-color 0.5s ease",
        background: scrolled ? "rgba(8,8,8,0.92)" : "transparent",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(255,255,255,0.08)",
        backdropFilter: scrolled ? "blur(20px)" : "none",
      }}
    >
      <div
        className="max-w-7xl mx-auto h-full flex items-center justify-between nav-desktop-pad"
        style={{ padding: "0 3rem" }}
      >
        {/* Logo */}
        <button
          onClick={() => handleNav("#hero")}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          <img
            src="/images/ascend-logo-new.png"
            alt="Ascend"
            style={{
              height: 28,
              width: "auto",
              objectFit: "contain",
            }}
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center" style={{ gap: "2.5rem" }}>
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.82rem",
                fontWeight: 400,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: 0,
                transition: "color 0.3s",
                position: "relative",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.9)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.45)"; }}
            >
              {link.label}
            </button>
          ))}

          {/* CTA */}
          <button
            onClick={() => handleNav("#contact")}
            style={{
              background: "none",
              border: "1px solid rgba(244,68,12,0.7)",
              cursor: "pointer",
              color: "#f4440c",
              fontSize: "0.82rem",
              fontWeight: 500,
              letterSpacing: "0.06em",
              padding: "0.6rem 1.6rem",
              transition: "all 0.3s",
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = "#f4440c";
              el.style.color = "#fff";
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = "none";
              el.style.color = "#f4440c";
            }}
          >
            {t.nav.getInTouch}
          </button>

          {/* Language switcher */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", marginLeft: "0.5rem" }}>
            {langOptions.map((opt, i) => (
              <>
                {i > 0 && <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.7rem" }}>|</span>}
                <button
                  key={opt.code}
                  onClick={() => setLang(opt.code)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: lang === opt.code ? "#f4440c" : "rgba(255,255,255,0.45)",
                    fontSize: "0.72rem",
                    fontWeight: lang === opt.code ? 600 : 400,
                    letterSpacing: "0.06em",
                    padding: "0.25rem 0.4rem",
                    transition: "color 0.3s",
                  }}
                >
                  {opt.label}
                </button>
              </>
            ))}
          </div>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.7)",
            padding: 4,
          }}
        >
          <Icon icon={mobileOpen ? "mdi:close" : "mdi:menu"} style={{ fontSize: 22 }} />
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: "rgba(8,8,8,0.97)",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              overflow: "hidden",
              backdropFilter: "blur(20px)",
            }}
          >
            <div style={{ padding: "1.5rem 1.25rem" }}>
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: "none",
                    border: "none",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "0.7rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    padding: "1rem 0",
                    transition: "color 0.3s",
                  }}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => handleNav("#contact")}
                style={{
                  marginTop: "1.25rem",
                  width: "100%",
                  background: "#f4440c",
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  padding: "0.85rem",
                }}
              >
                {t.nav.getInTouch}
              </button>
              {/* Mobile language switcher */}
              <div style={{ display: "flex", justifyContent: "center", gap: "1rem", marginTop: "1rem" }}>
                {langOptions.map((opt) => (
                  <button
                    key={opt.code}
                    onClick={() => setLang(opt.code)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: lang === opt.code ? "#f4440c" : "rgba(255,255,255,0.4)",
                      fontSize: "0.72rem",
                      fontWeight: lang === opt.code ? 600 : 400,
                      letterSpacing: "0.1em",
                      padding: "0.5rem",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
